import {
  EFFECT_PRESET_SOURCE_GLOBAL,
  createEffectGlobalPresetExport,
} from '@/editor/effects/effectPresets'
import {
  TEXT_MOTION_PRESET_SOURCE_GLOBAL,
  createTextMotionGlobalPresetExport,
  normalizeTextMotionPreset,
} from '@/editor/text/textMotionPresets'
import { normalizeWorkflowPreset } from '@/etc/aiWorkflowPresets'

export const VYBY_PRESETS_TABLE = 'vyby_presets'

export const VYBY_PRESET_KIND_EFFECT = 'effect'
export const VYBY_PRESET_KIND_TEXT_MOTION = 'text_motion'
export const VYBY_PRESET_KIND_NODE_WORKFLOW = 'node_workflow'

export const VYBY_PRESET_KINDS = [
  VYBY_PRESET_KIND_EFFECT,
  VYBY_PRESET_KIND_TEXT_MOTION,
  VYBY_PRESET_KIND_NODE_WORKFLOW,
]

export const VYBY_PRESET_SOURCE_GLOBAL = 'global'

const KIND_ALIASES = {
  effect: VYBY_PRESET_KIND_EFFECT,
  effects: VYBY_PRESET_KIND_EFFECT,
  effect_preset: VYBY_PRESET_KIND_EFFECT,
  text: VYBY_PRESET_KIND_TEXT_MOTION,
  text_motion: VYBY_PRESET_KIND_TEXT_MOTION,
  text_animation: VYBY_PRESET_KIND_TEXT_MOTION,
  text_animations: VYBY_PRESET_KIND_TEXT_MOTION,
  workflow: VYBY_PRESET_KIND_NODE_WORKFLOW,
  workflows: VYBY_PRESET_KIND_NODE_WORKFLOW,
  node_workflow: VYBY_PRESET_KIND_NODE_WORKFLOW,
  node_workflows: VYBY_PRESET_KIND_NODE_WORKFLOW,
}

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, next]) => {
    if (typeof next !== 'undefined') acc[key] = next
    return acc
  }, {})

const firstString = (...values) => {
  for (const value of values) {
    const next = `${value || ''}`.trim()
    if (next) return next
  }
  return ''
}

export const normalizeVybyPresetKind = (kind) => {
  const raw = `${kind || ''}`.trim().toLowerCase()
  return KIND_ALIASES[raw] || ''
}

export const normalizeVybyPresetTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((tag) => `${tag || ''}`.trim())
      .filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
  }
  return []
}

const normalizeSortOrder = (value) => {
  const next = Number(value)
  return Number.isFinite(next) ? Math.floor(next) : null
}

const normalizePayloadObject = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  return cloneJson(payload) || null
}

export const normalizeVybyPresetRow = (row = {}) => {
  if (!row || typeof row !== 'object') return null
  const payload = normalizePayloadObject(row.payload)
  if (!payload) return null

  const kind = normalizeVybyPresetKind(row.kind || payload.kind)
  if (!kind) return null

  const stableId = firstString(
    row.stable_id,
    row.stableId,
    row.code,
    payload.stable_id,
    payload.stableId,
    payload.id,
    payload.code
  )
  const name = firstString(row.name, payload.name)
  if (!stableId || !name) return null

  return compactObject({
    id: firstString(row.id),
    kind,
    stableId,
    stable_id: stableId,
    name,
    description:
      typeof row.description === 'string'
        ? row.description
        : typeof payload.description === 'string'
          ? payload.description
          : '',
    intentTags: normalizeVybyPresetTags(row.intent_tags || row.intentTags || payload.intentTags),
    intent_tags: normalizeVybyPresetTags(row.intent_tags || row.intentTags || payload.intentTags),
    image: firstString(row.image, row.img, payload.image, payload.img),
    img: firstString(row.image, row.img, payload.image, payload.img),
    category: firstString(row.category, payload.category),
    group: firstString(row.group, payload.group),
    payload,
    enabled: row.enabled !== false,
    sortOrder: normalizeSortOrder(row.sort_order ?? row.sortOrder),
    sort_order: normalizeSortOrder(row.sort_order ?? row.sortOrder),
    createdAt: firstString(row.created_at, row.createdAt),
    created_at: firstString(row.created_at, row.createdAt),
    updatedAt: firstString(row.updated_at, row.updatedAt),
    updated_at: firstString(row.updated_at, row.updatedAt),
  })
}

export const normalizeVybyPresetRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map(normalizeVybyPresetRow)
    .filter(Boolean)
    .sort((a, b) => {
      const ao = a.sortOrder === null ? Number.POSITIVE_INFINITY : a.sortOrder
      const bo = b.sortOrder === null ? Number.POSITIVE_INFINITY : b.sortOrder
      if (ao !== bo) return ao - bo
      return `${a.name || ''}`.localeCompare(`${b.name || ''}`)
    })

export const groupVybyPresetRowsByKind = (rows = []) =>
  normalizeVybyPresetRows(rows).reduce((acc, row) => {
    if (!acc[row.kind]) acc[row.kind] = []
    acc[row.kind].push(row)
    return acc
  }, {})

export const cmsPresetRowForSave = (preset = {}) => {
  const row = normalizeVybyPresetRow({
    ...preset,
    payload: preset.payload || {},
  })
  if (!row) return null

  return {
    kind: row.kind,
    stable_id: row.stableId,
    name: row.name,
    description: row.description || '',
    intent_tags: row.intentTags || [],
    image: row.image || '',
    category: row.category || '',
    group: row.group || '',
    payload: row.payload || {},
    enabled: row.enabled !== false,
    sort_order: row.sortOrder,
  }
}

export const vybyPresetRowToEffectPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_EFFECT) return null

  const source = normalized.payload || {}
  const presetPayload =
    source.preset && typeof source.preset === 'object'
      ? source.preset
      : source
  const group = firstString(normalized.group, source.group, source.preset?.group)
  if (!group || !presetPayload || typeof presetPayload !== 'object') return null

  return createEffectGlobalPresetExport({
    id: normalized.stableId,
    name: normalized.name,
    group,
    img: normalized.image,
    description: normalized.description,
    intentTags: normalized.intentTags,
    preset: presetPayload,
  })
}

export const vybyPresetRowsToEffectPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToEffectPreset)
    .filter(Boolean)

export const vybyPresetRowToTextMotionPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_TEXT_MOTION) return null

  const payload = normalized.payload || {}
  return normalizeTextMotionPreset(
    createTextMotionGlobalPresetExport({
      ...payload,
      id: normalized.stableId,
      name: normalized.name,
      description: normalized.description,
      intentTags: normalized.intentTags,
    })
  )
}

export const vybyPresetRowsToTextMotionPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToTextMotionPreset)
    .filter(Boolean)

export const vybyPresetRowToNodeWorkflowPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_NODE_WORKFLOW) return null

  const source = normalized.payload || {}
  const graph =
    source.graph && typeof source.graph === 'object'
      ? source.graph
      : source
  const preset = normalizeWorkflowPreset({
    ...source,
    id: normalized.stableId,
    name: normalized.name,
    description: normalized.description,
    category: normalized.category || source.category,
    createdAt: normalized.createdAt || source.createdAt,
    updatedAt: normalized.updatedAt || source.updatedAt,
    graph,
  })
  if (!preset) return null

  return {
    ...preset,
    source: VYBY_PRESET_SOURCE_GLOBAL,
    intentTags: normalized.intentTags,
    image: normalized.image,
    img: normalized.image,
  }
}

export const vybyPresetRowsToNodeWorkflowPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToNodeWorkflowPreset)
    .filter(Boolean)

export const vybyPresetRowToDomainPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized) return null
  if (normalized.kind === VYBY_PRESET_KIND_EFFECT) {
    return vybyPresetRowToEffectPreset(normalized)
  }
  if (normalized.kind === VYBY_PRESET_KIND_TEXT_MOTION) {
    return vybyPresetRowToTextMotionPreset(normalized)
  }
  if (normalized.kind === VYBY_PRESET_KIND_NODE_WORKFLOW) {
    return vybyPresetRowToNodeWorkflowPreset(normalized)
  }
  return null
}

export const vybyPresetRowsToDomainPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToDomainPreset)
    .filter(Boolean)

export const isVybyGlobalEffectPreset = (preset = {}) =>
  preset?.source === EFFECT_PRESET_SOURCE_GLOBAL

export const isVybyGlobalTextMotionPreset = (preset = {}) =>
  preset?.source === TEXT_MOTION_PRESET_SOURCE_GLOBAL
